import type React from 'react';

/**
 * Custom React Component Registry
 *
 * Maps CMS reactComponentSlug values to actual React components.
 * This registry is used when the CMS specifies a custom_react_component
 * block that should render a specific React component by its slug.
 *
 * Usage:
 * 1. Import your component
 * 2. Register it with: registerCustomComponent('your-slug', YourComponent)
 * 3. CMS can then reference it via reactComponentSlug: 'your-slug'
 */

// Type for custom component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CustomComponent = React.FC<any>;

// Registry map
const customComponentRegistry: Record<string, CustomComponent> = {};

/**
 * Get a custom component by its slug
 * @param slug - The reactComponentSlug from CMS
 * @returns The React component or null if not found
 */
export const getCustomComponent = (slug: string): CustomComponent | null => {
  return customComponentRegistry[slug] ?? null;
};

/**
 * Check if a custom component exists for a given slug
 * @param slug - The reactComponentSlug from CMS
 * @returns boolean
 */
export const hasCustomComponent = (slug: string): boolean => {
  return slug in customComponentRegistry;
};

/**
 * Register a custom component for a slug
 * @param slug - The slug to register
 * @param component - The React component to register
 */
export const registerCustomComponent = (
  slug: string,
  component: CustomComponent
): void => {
  if (
    customComponentRegistry[slug] !== null &&
    customComponentRegistry[slug] !== undefined
  ) {
    console.warn(
      `[CustomComponentRegistry] Overwriting existing component for slug: "${slug}"`
    );
  }
  customComponentRegistry[slug] = component;
};

/**
 * Unregister a custom component
 * @param slug - The slug to unregister
 */
export const unregisterCustomComponent = (slug: string): void => {
  delete customComponentRegistry[slug];
};

/**
 * Get all registered custom component slugs
 * @returns Array of registered slugs
 */
export const getRegisteredCustomSlugs = (): string[] => {
  return Object.keys(customComponentRegistry);
};

/**
 * Bulk register multiple custom components
 * @param components - Object mapping slugs to components
 */
export const registerCustomComponents = (
  components: Record<string, CustomComponent>
): void => {
  Object.entries(components).forEach(([slug, component]) => {
    registerCustomComponent(slug, component);
  });
};

export default customComponentRegistry;
